import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/context/AuthContext';
import { Bell, Mail, MessageSquare, ShieldAlert, Loader2, Send, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationSettingsPage() {
  const { token } = useAuth();
  const auth = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    const r = await fetch(`${API}/notifications/preferences`, { headers: auth });
    if (r.ok) setPrefs(await r.json());
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (patch) => {
    setBusy(true);
    const r = await fetch(`${API}/notifications/preferences`, {
      method: 'PUT', headers: auth, body: JSON.stringify(patch),
    });
    if (r.ok) setPrefs(await r.json());
    setBusy(false);
  };

  const testPush = async () => {
    setBusy(true); setTestResult(null);
    const r = await fetch(`${API}/notifications/test-push`, { method: 'POST', headers: auth });
    setTestResult(await r.json());
    setBusy(false);
  };

  if (!prefs) {
    return <div className="flex items-center gap-2 text-sm text-slate-500 p-4"><Loader2 className="w-4 h-4 animate-spin" />Loading preferences…</div>;
  }

  return (
    <div className="max-w-3xl space-y-6" data-testid="notif-settings-page">
      <div>
        <h1 className="text-3xl font-semibold flex items-center gap-2"><Bell className="w-7 h-7 text-indigo-500" />Notifications</h1>
        <p className="text-sm text-slate-500 mt-1">Choose how you want to hear about fraud alerts and tracking updates when you're offline.</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-rose-500" />Fraud alerts</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" />Email alerts</p>
              <p className="text-xs text-slate-500 mt-0.5">Send an email whenever a shipment is flagged for review or blocked.</p>
            </div>
            <Switch
              checked={!!prefs.email_alerts}
              onCheckedChange={(v) => save({ email_alerts: v })}
              disabled={busy}
              data-testid="pref-email-alerts"
            />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium flex items-center gap-2"><MessageSquare className="w-4 h-4 text-slate-400" />SMS alerts (block only)</p>
              <p className="text-xs text-slate-500 mt-0.5">Text me only when a shipment is BLOCKED. Requires a phone number below.</p>
            </div>
            <Switch
              checked={!!prefs.sms_alerts}
              onCheckedChange={(v) => save({ sms_alerts: v })}
              disabled={busy || !prefs.sms_phone}
              data-testid="pref-sms-alerts"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-400">SMS phone (E.164, e.g. +15551234567)</label>
            <div className="flex gap-2 mt-1">
              <Input
                type="tel"
                placeholder="+15551234567"
                defaultValue={prefs.sms_phone || ''}
                onBlur={(e) => e.target.value !== (prefs.sms_phone || '') && save({ sms_phone: e.target.value })}
                data-testid="pref-sms-phone"
                className="max-w-xs"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Minimum severity for email</label>
            <select
              className="mt-1 h-9 px-3 rounded-md border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm"
              value={prefs.min_severity}
              onChange={(e) => save({ min_severity: e.target.value })}
              disabled={busy}
              data-testid="pref-min-severity"
            >
              <option value="review">Review + Block</option>
              <option value="block">Block only</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Test push</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-500">Send a synthetic BLOCK alert to yourself right now to confirm delivery.</p>
          <Button onClick={testPush} disabled={busy} data-testid="pref-test-push">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send test alert
          </Button>
          {testResult && (
            <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-800 text-xs space-y-1" data-testid="pref-test-result">
              <div className="flex items-center gap-2">
                {testResult.email?.status === 'sent' ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <AlertTriangle className="w-4 h-4 text-amber-500" />}
                <b>Email:</b> {testResult.email ? (testResult.email.status || JSON.stringify(testResult.email).slice(0, 120)) : 'skipped'}
              </div>
              <div className="flex items-center gap-2">
                {testResult.sms?.sent ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <AlertTriangle className="w-4 h-4 text-amber-500" />}
                <b>SMS:</b> {testResult.sms ? (testResult.sms.sent ? `Sent (${testResult.sms.sid})` : (testResult.sms.error || testResult.sms.reason || 'not sent')) : 'skipped (SMS disabled or no phone)'}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
